import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { Button, Offcanvas, OffcanvasBody, OffcanvasHeader, Spinner } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { MODALS } from '@/configs/modals';
import { useModals } from '@/hooks/useModals';
import { fetchUsersListApi } from '@/store/users/actions';
import { fetchUserApi } from '@/store/user/actions';
import {
  cancelNutritionConsultation,
  fetchNutritionConsultation,
  fetchNutritionistSchedulersApi,
  fetchNutritionists,
  submitNutritionConsultation,
} from '@/store/nutrition/actions';
import { getNutritionConsultationPageData } from '@/utils/selectors';

const SERVICE_TYPES = ['standard', 'tester', 'support'];

const getName = (item) => item?.fullname || item?.fullname_uk || item?.fullname_ru || `#${item?.id}`;
const getId = (item) => item?.id || item;
const getSlotOption = (slot) => ({
  value: slot?.id,
  label: `${slot?.start_datetime || slot?.start} - ${slot?.end_datetime || slot?.end}`,
});

const NutritionConsultationManageDrawer = () => {
  const modalId = MODALS.nutritionConsultation;
  const { params, modalClose, modalOpen } = useModals(modalId);
  const { isOpen, id, initSchedule, initNutritionistId, onUpdated } = params || {};
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data: consultation } = useSelector(getNutritionConsultationPageData);
  const isEdit = !!id;

  const [users, setUsers] = useState([]);
  const [nutritionists, setNutritionists] = useState([]);
  const [slots, setSlots] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [selectedUser, setSelectedUser] = useState();
  const [selectedNutritionist, setSelectedNutritionist] = useState();
  const [selectedSlot, setSelectedSlot] = useState();
  const [selectedType, setSelectedType] = useState();
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const close = () => modalClose(modalId);
  const selectedConsultation = Number(consultation?.id) === Number(id) ? consultation : undefined;

  const setAvailableServiceTypes = (user, currentType) => {
    const availableTypes = new Set((user?.nutrition_consultations || [])
      .filter((item) => item?.status === 'bought' && !item?.schedule && !item?.nutritionist)
      .map((item) => item?.service_type || item?.service?.type)
      .filter(Boolean));
    const values = [...new Set([...SERVICE_TYPES, currentType].filter(Boolean))];

    setServiceTypes(values.map((value) => ({
      value,
      label: value,
      isDisabled: !availableTypes.has(value) && value !== currentType,
    })));
  };

  const fetchUserServices = async (userId, currentType) => {
    if (!userId) return;
    try {
      const user = await fetchUserApi(userId);
      setAvailableServiceTypes(user, currentType);
    } catch (error) {
      setServiceTypes(SERVICE_TYPES.map((value) => ({ value, label: value })));
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setUsers([]);
      setNutritionists([]);
      setSlots([]);
      setServiceTypes([]);
      setSelectedUser(undefined);
      setSelectedNutritionist(undefined);
      setSelectedSlot(undefined);
      setSelectedType(undefined);
      return;
    }

    setIsLoadingCatalogs(true);
    Promise.all([fetchUsersListApi(), fetchNutritionists().payload])
      .then(([usersData, nutritionistsData]) => {
        setUsers((usersData || []).map((item) => ({
          value: item.id,
          label: `${getName(item)} (${item.username || item.email || item.id})`,
        })));
        setNutritionists((nutritionistsData || []).map((item) => ({
          value: item.id,
          label: getName(item),
        })));
      })
      .finally(() => setIsLoadingCatalogs(false));
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && id) dispatch(fetchNutritionConsultation(id));
  }, [dispatch, id, isOpen]);

  useEffect(() => {
    if (!isOpen || !selectedConsultation) return;

    const userId = getId(selectedConsultation.user);
    const nutritionistId = getId(selectedConsultation.nutritionist);
    const serviceType = selectedConsultation.service_type || selectedConsultation.service?.type;
    const userOption = users.find((item) => Number(item.value) === Number(userId)) || {
      value: userId,
      label: getName(selectedConsultation.user),
    };
    const nutritionistOption = nutritionists.find((item) => Number(item.value) === Number(nutritionistId)) || {
      value: nutritionistId,
      label: getName(selectedConsultation.nutritionist),
    };

    setSelectedUser(userOption);
    setSelectedNutritionist(nutritionistOption);
    setSelectedSlot(getSlotOption(selectedConsultation.schedule));
    setSelectedType(serviceType ? { value: serviceType, label: serviceType } : undefined);
    fetchUserServices(userId, serviceType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedConsultation]);

  useEffect(() => {
    if (!isOpen || id || !initSchedule?.id) return;
    setSelectedSlot(getSlotOption(initSchedule));
    const nutritionist = nutritionists.find((item) => Number(item.value) === Number(initNutritionistId));
    if (nutritionist) setSelectedNutritionist(nutritionist);
  }, [id, initNutritionistId, initSchedule, isOpen, nutritionists]);

  useEffect(() => {
    if (!isOpen || !selectedNutritionist?.value || initSchedule?.id) return;

    setIsLoadingSlots(true);
    fetchNutritionistSchedulersApi(selectedNutritionist.value)
      .then((data) => setSlots((data?.schedulers || []).map(getSlotOption)))
      .catch(() => setSlots([]))
      .finally(() => setIsLoadingSlots(false));
  }, [initSchedule, isOpen, selectedNutritionist?.value]);

  const handleChangeUser = (item) => {
    setSelectedUser(item);
    setSelectedType(undefined);
    fetchUserServices(item?.value);
  };

  const handleChangeNutritionist = (item) => {
    setSelectedNutritionist(item);
    if (!initSchedule?.id) setSelectedSlot(undefined);
  };

  const slotOptions = useMemo(() => {
    if (!selectedSlot?.value || slots.some((item) => Number(item.value) === Number(selectedSlot.value))) {
      return slots;
    }
    return [selectedSlot, ...slots];
  }, [selectedSlot, slots]);

  const submit = async () => {
    if (!selectedUser || !selectedSlot || !selectedType) {
      toast.error(t('nutrition.select_required'));
      return;
    }

    setIsSubmitting(true);
    await submitNutritionConsultation({
      id,
      data: {
        scheduler: selectedSlot.value,
        services_type: selectedType.value,
        user: selectedUser.value,
      },
      dispatch,
      onSuccess: () => {
        toast.success(t('nutrition.saved'));
        onUpdated?.();
        close();
      },
      onError: () => toast.error(t('nutrition.save_error')),
    });
    setIsSubmitting(false);
  };

  const cancel = () => {
    modalOpen(MODALS.confirm, {
      isOpen: true,
      text: t('nutrition.confirm_cancel', { id }),
      isDeleteType: true,
      onOk: () => {
        setIsSubmitting(true);
        dispatch(cancelNutritionConsultation(id, {
          t,
          onSuccess: () => {
            onUpdated?.();
            close();
          },
          onError: () => setIsSubmitting(false),
        }));
      },
    });
  };

  if (!isOpen) return null;

  return (
    <Offcanvas isOpen={isOpen} toggle={close} direction="end" className={modalId}>
      <OffcanvasHeader toggle={close}>
        {isEdit ? `${t('nutrition.reschedule')} #${id}` : t('nutrition.schedule')}
      </OffcanvasHeader>
      <OffcanvasBody>
        {(isLoadingCatalogs || isLoadingSlots) && <Spinner color="primary" className="mb-3" />}
        <label className="form-label">{t('consultations.client')}</label>
        <Select value={selectedUser} options={users} onChange={handleChangeUser} className="mb-3" />
        <label className="form-label">{t('nutrition.specialist')}</label>
        <Select value={selectedNutritionist} options={nutritionists} onChange={handleChangeNutritionist} className="mb-3" isDisabled={Boolean(initSchedule?.id)} />
        <label className="form-label">{t('nutrition.service_type')}</label>
        <Select value={selectedType} options={serviceTypes} onChange={setSelectedType} className="mb-3" />
        <label className="form-label">{t('nutrition.slot')}</label>
        <Select value={selectedSlot} options={slotOptions} onChange={setSelectedSlot} className="mb-3" isDisabled={Boolean(initSchedule?.id)} />
        <div className="d-flex justify-content-end gap-2">
          {isEdit && <Button color="danger" onClick={cancel} disabled={isSubmitting}>{t('consultation.modal.btn_cancel')}</Button>}
          <Button color="light" onClick={close}>{t('btn_cancel')}</Button>
          <Button color="primary" onClick={submit} disabled={isSubmitting}>
            {isSubmitting && <Spinner size="sm" className="me-2" />}
            {t('btn_save')}
          </Button>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default NutritionConsultationManageDrawer;
